import { EventEmitter } from '@spotify-internal/emitter';
import { Reason } from '@js-sdk/playback/lib/enums/reason';
import { LOCAL_DEVICE_ID, } from '../typedefs';
import { HarmonyEvent } from '../enums/harmony_event';
import { HarmonyErrors } from '../enums/errors';
import { HarmonyErrorSource } from '../enums/error_source';
import { HarmonyError } from '../error/harmony_error';
import { defer } from './helpers/defer';
import { areDeviceListsEqual } from './helpers/device';
import { queueTrackFromQid, } from './player_queue';
const DEFAULT_SEEK_TIME = 15000;
/**
 * Playback and Connect for Web-enabled platforms.
 */
export class Harmony extends EventEmitter {
    constructor(options) {
        super();
        this._hasFakeState = false;
        this._localPlayerEnabled = false;
        this._localPlayback = false;
        this._lastDeviceList = [];
        this._deferredStreamer = defer();
        this._client = options.client;
        this._streamer = options.streamer;
        this._controller = options.controller;
        this._hidden = !!options.hidden;
        this._onlyLocalState = !!options.onlyLocalState;
        this._preferController = this._onlyLocalState
            ? false
            : !!options.preferControllerState;
        this._claimInactivePlayerStates = !!options.claimInactivePlayerStates;
        this._enableControllerWithoutStreamer = !!options.enableControllerWithoutStreamer;
        this._autoActivateElement = !!options.autoActivateElement;
        this._deactivateOnStop = !!options.experimentalDeactivateOnStop;
        this._init();
    }
    static create(options) {
        return new Harmony(options);
    }
    _init() {
        this._createStreamerDeferred();
        // Client Events
        const client = this._client;
        client.on("error" /* CLIENT_ERROR */, this._onError.bind(this));
        this.proxyEmit(client, "authenticated" /* CLIENT_AUTHENTICATED */, HarmonyEvent.AUTHENTICATED);
        this.proxyEmit(client, "authentication_error" /* CLIENT_AUTHENTICATION_ERROR */, HarmonyEvent.AUTHENTICATION_ERROR);
        this.proxyEmit(client, "before_disconnect" /* CLIENT_BEFORE_DISCONNECT */, HarmonyEvent.BEFORE_DISCONNECT);
        this.proxyEmit(client, "connected" /* CLIENT_CONNECTED */, HarmonyEvent.CONNECTED);
        this.proxyEmit(client, "connection_error" /* CLIENT_CONNECTION_ERROR */, HarmonyEvent.CONNECTION_ERROR);
        this.proxyEmit(client, "descriptor_changed" /* CLIENT_DEVICE_DESCRIPTOR_CHANGED */, HarmonyEvent.DEVICE_DESCRIPTOR_CHANGED);
        this.proxyEmit(client, "product_state_changed" /* CLIENT_PRODUCT_STATE_CHANGED */, HarmonyEvent.PRODUCT_STATE_CHANGED);
        this.proxyEmit(client, "reconnected" /* CLIENT_RECONNECTED */, HarmonyEvent.RECONNECTED);
        this.proxyEmit(client, "reconnecting" /* CLIENT_RECONNECTING */, HarmonyEvent.RECONNECTING);
        this.proxyEmit(client, "unrecoverable_failure" /* CLIENT_UNRECOVERABLE_FAILURE */, HarmonyEvent.UNRECOVERABLE_FAILURE);
        this.proxyEmit(client, "logged_out" /* CLIENT_LOGGED_OUT */, HarmonyEvent.LOGGED_OUT);
        // Controller Events
        const controller = this._controller;
        const _onDeviceStateChanged = (e) => {
            this._onDeviceStateChanged(e.data);
        };
        controller.on("device_info_changed" /* CONTROLLER_DEVICE_INFO_CHANGED */, _onDeviceStateChanged);
        controller.on("devices_changed" /* CONTROLLER_DEVICES_CHANGED */, this._onDevicesChanged.bind(this));
        controller.on("state_changed" /* CONTROLLER_STATE_CHANGED */, this._onPlayerStateChanged.bind(this));
        controller.on("progress" /* CONTROLLER_PROGRESS */, this._onControllerProgress.bind(this));
        this.proxyEmitAll(controller, {
            ["max_subscriptions_reached" /* CONTROLLER_MAX_SUBSCRIPTIONS_REACHED */]: HarmonyEvent.MAX_SUBSCRIPTIONS_REACHED,
            ["device_deactivated" /* CONTROLLER_DEVICE_DEACTIVATED */]: HarmonyEvent.DEVICE_DEACTIVATED,
        });
        controller.on("registered" /* CONTROLLER_REGISTERED */, this.emit.bind(this, HarmonyEvent.REMOTE_OBSERVER_ENABLED, null));
        controller.on("deregistered" /* CONTROLLER_DEREGISTERED */, this.emit.bind(this, HarmonyEvent.REMOTE_OBSERVER_DISABLED, null));
        const streamer = this._streamer;
        if (!streamer) {
            // Streamer is disabled.
            this._disableLocalTarget();
            this.on(HarmonyEvent.AUTHENTICATED, () => {
                this.emit(HarmonyEvent.LOCAL_PLAYER_DISABLED, null);
            });
        }
        else {
            streamer.on("before_volume_change" /* STREAMER_BEFORE_VOLUME_CHANGE */, this._onStreamerBeforeVolumeChange.bind(this));
            streamer.on("deregistered" /* STREAMER_DEREGISTERED */, this._onStreamerDeregistered.bind(this));
            streamer.on("registered" /* STREAMER_REGISTERED */, this._onStreamerRegistered.bind(this));
            streamer.on("registration_aborted" /* STREAMER_REGISTRATION_ABORTED */, this._onStreamerRegistrationAborted.bind(this));
            streamer.on("state_changed" /* STREAMER_STATE_CHANGED */, this._onStreamerStateChanged.bind(this));
            streamer.on("stopped_on_background" /* STREAMER_STOPPED_ON_BACKGROUND */, this._onStreamerStoppedOnBackground.bind(this));
            this.proxyEmit(streamer, "autoplay_failed" /* STREAMER_AUTOPLAY_FAILED */, HarmonyEvent.AUTOPLAY_FAILED);
            this.proxyEmit(streamer, "buffering_end" /* STREAMER_BUFFERING_END */, HarmonyEvent.BUFFERING_END);
            this.proxyEmit(streamer, "buffering_start" /* STREAMER_BUFFERING_START */, HarmonyEvent.BUFFERING_START);
            this.proxyEmit(streamer, "duration_changed" /* STREAMER_DURATION_CHANGED */, HarmonyEvent.DURATION_CHANGED);
            this.proxyEmit(streamer, "max_list_errors_reached" /* STREAMER_MAX_LIST_ERRORS_REACHED */, HarmonyEvent.MAX_LIST_ERRORS_REACHED);
            this.proxyEmit(streamer, "max_subscriptions_reached" /* STREAMER_MAX_SUBSCRIPTIONS_REACHED */, HarmonyEvent.MAX_SUBSCRIPTIONS_REACHED);
            this.proxyEmit(streamer, "playback_capped" /* STREAMER_PLAYBACK_CAPPED */, HarmonyEvent.PLAYBACK_CAPPED);
            this.proxyEmit(streamer, "player_initialization_done" /* STREAMER_PLAYER_INITIALIZATION_DONE */, HarmonyEvent.PLAYER_INITIALIZATION_DONE);
            this.proxyEmit(streamer, "player_initialization_failed" /* STREAMER_PLAYER_INITIALIZATION_FAILED */, HarmonyEvent.PLAYER_INITIALIZATION_FAILED);
            this.proxyEmit(streamer, "progress" /* STREAMER_PROGRESS */, HarmonyEvent.PROGRESS);
            this.proxyEmit(streamer, "registration_error" /* STREAMER_REGISTRATION_ERROR */, HarmonyEvent.SERVICE_REGISTRATION_ERROR);
            this.proxyEmit(streamer, "track_ended" /* STREAMER_TRACK_ENDED */, HarmonyEvent.TRACK_ENDED);
            this.proxyEmit(streamer, "video_element_appended" /* STREAMER_VIDEO_ELEMENT_APPENDED */, HarmonyEvent.VIDEO_ELEMENT_APPENDED);
            this.proxyEmit(streamer, "video_element_removed" /* STREAMER_VIDEO_ELEMENT_REMOVED */, HarmonyEvent.VIDEO_ELEMENT_REMOVED);
            streamer.on("context_ended" /* STREAMER_CONTEXT_ENDED */, this.emit.bind(this, HarmonyEvent.LOCAL_CONTEXT_ENDED, null));
            streamer.on("logged_out" /* STREAMER_LOGGED_OUT */, this.emit.bind(this, HarmonyEvent.LOCAL_PLAYER_LOGGED_OUT, null));
        }
    }
    _createStreamerDeferred() {
        this._deferredStreamer = defer();
        this._deferredStreamer.promise.catch(function () {
            // Noop
        });
    }
    _isTargetIdLocal(deviceId) {
        if (!deviceId) {
            return Promise.resolve(!!this._localPlayback);
        }
        if (deviceId === LOCAL_DEVICE_ID) {
            return Promise.resolve(true);
        }
        return this._client.getDeviceDescriptor().then(function (descriptor) {
            return deviceId === descriptor.getId();
        });
    }
    _onStreamerBeforeVolumeChange(e) {
        const ev = this.emitSync(HarmonyEvent.BEFORE_VOLUME_CHANGE, {
            volume: e.data.volume,
        });
        if (ev.defaultPrevented) {
            e.preventDefault();
        }
    }
    _onStreamerRegistered(e) {
        this._deferredStreamer.resolve(e.data);
        this._createStreamerDeferred();
        this._deferredStreamer.resolve(e.data);
        this._localPlayerEnabled = true;
        this._controller.register();
        this.emit(HarmonyEvent.LOCAL_PLAYER_ENABLED, null);
    }
    _onStreamerDeregistered() {
        this._disableLocalTarget();
        this.emit(HarmonyEvent.LOCAL_PLAYER_DISABLED, null);
    }
    _onStreamerRegistrationAborted() {
        this._disableLocalTarget();
        if (this._enableControllerWithoutStreamer) {
            this._controller.register();
        }
        this.emit(HarmonyEvent.LOCAL_PLAYER_DISABLED, null);
    }
    _disableLocalTarget() {
        this._localPlayerEnabled = false;
        const error = new HarmonyError(HarmonyErrors.HARMONY_LOCAL_PLAYER_DISABLED, 'Cannot perform command; local player is disabled.');
        this._deferredStreamer.reject(error);
        this._createStreamerDeferred();
        this._deferredStreamer.reject(error);
    }
    _onStreamerConnect() {
        return this._deferredStreamer.promise;
    }
    _onDeviceStateChanged(data) {
        const hadLocalPlayback = !!this._localPlayback;
        const generateLocalDeviceChange = (overrideActive = false) => {
            this._generateLocalDeviceInfo(overrideActive).then((localDeviceInfo) => {
                this.emit(HarmonyEvent.LAST_ACTIVE_DEVICE_INFO_CHANGED, {
                    deviceInfo: localDeviceInfo,
                    hasOrphanedState: false,
                });
            });
        };
        if (this._localPlayerEnabled &&
            data.hasOrphanedState &&
            this._claimInactivePlayerStates) {
            this._hasFakeState = true;
            generateLocalDeviceChange(true);
            return;
        }
        this._hasFakeState = false;
        this._localPlayback = !!(data.deviceInfo && data.deviceInfo.local);
        if (this._localPlayback) {
            if (!this._streamer) {
                throw new HarmonyError(HarmonyErrors.HARMONY_OPERATION_FORBIDDEN, 'No streamer provided.');
            }
            // Ensure that volume is always in-sync
            this._streamer
                .getVolume()
                .then((volume) => {
                if (data.deviceInfo) {
                    data.deviceInfo.volume = volume;
                }
                this.emit(HarmonyEvent.LAST_ACTIVE_DEVICE_INFO_CHANGED, data);
            })
                .catch(function () {
                // No-op
            });
        }
        else if (this._onlyLocalState) {
            // Only propagate the change if it somehow affects the local device
            // (i.e.: it holds, obtained or lost playback) and instead of emiting the
            // state of the device whose info has changed, emit the info of the
            // local device
            if (this._localPlayback || hadLocalPlayback) {
                generateLocalDeviceChange();
            }
        }
        else {
            this.emit(HarmonyEvent.LAST_ACTIVE_DEVICE_INFO_CHANGED, data);
        }
    }
    _onDevicesChanged(e) {
        const previousList = this._lastDeviceList;
        this._parseDeviceList(e.data).then((devices) => {
            if (areDeviceListsEqual(previousList, devices)) {
                return;
            }
            this.emit(HarmonyEvent.DEVICES_CHANGED, {
                devices: devices,
                localDevice: null,
            });
        });
    }
    _onError(event) {
        const error = event.data.error;
        if (event.data.source === HarmonyErrorSource.PLAYBACK &&
            error &&
            error.unrecoverable) {
            this._disableLocalTarget();
            if (this._streamer) {
                this._streamer.deregister();
            }
        }
        this.emit(HarmonyEvent.ERROR, event.data);
    }
    _onPlayerStateChanged(e) {
        if (this._onlyLocalState) {
            return;
        }
        if (this._preferController || !this._localPlayback) {
            let state;
            if (this._localPlayerEnabled && this._claimInactivePlayerStates) {
                state = e.data.state;
            }
            else {
                state = e.data.orphaned ? null : e.data.state;
            }
            this.emit(HarmonyEvent.STATE_CHANGED, {
                state: state,
            });
        }
    }
    _onControllerProgress(e) {
        // Only emit progress if the playback is not local.
        if (this._onlyLocalState) {
            return;
        }
        if (!this._localPlayback) {
            this.emit(HarmonyEvent.PROGRESS, e.data);
        }
    }
    _onStreamerStateChanged(e) {
        if (this._preferController) {
            return;
        }
        if (this._onlyLocalState) {
            this._triggerLocalStateChanges(e.data);
        }
        else if (this._hidden) {
            this._handleHidden(e.data);
        }
        else if (this._localPlayback) {
            this.emit(HarmonyEvent.STATE_CHANGED, e.data);
        }
    }
    _onStreamerStoppedOnBackground() {
        if (this._deactivateOnStop) {
            this._localPlayback = false;
        }
        this.emit(HarmonyEvent.STOPPED_ON_BACKGROUND, null);
    }
    _handleHidden(data) {
        if (data.state) {
            this._controller.suppressEvents();
            this._triggerLocalStateChanges(data);
        }
        else {
            this._controller.unsuppressEvents();
        }
    }
    _parseDeviceList(deviceList) {
        const devices = deviceList.devices
            .slice(0)
            .map((device) => Promise.resolve(device));
        if (!devices.length || !deviceList.localDevice) {
            devices.push(this._generateLocalDeviceInfo(this._hasFakeState));
        }
        else if (this._hasFakeState && deviceList.localDevice) {
            deviceList.localDevice.is_active = true;
        }
        return Promise.all(devices).then((list) => {
            this._lastDeviceList = list;
            return list;
        });
    }
    _triggerLocalStateChanges(data) {
        this._generateLocalDeviceInfo().then((deviceInfo) => {
            this._onDeviceStateChanged({
                deviceInfo: deviceInfo,
                hasOrphanedState: false,
            });
        });
        this.emit(HarmonyEvent.STATE_CHANGED, data);
    }
    /**
     * Checks whether harmony has any restriction to skip the current track and
     * start a new context
     *
     * @param deviceId - The device id
     * @return A promise resolved in case it can start a new context,
     *  or rejected in case it can't
     */
    _canStartNewContext(deviceId) {
        return this._runOnDevice(deviceId, () => {
            if (!this._streamer) {
                return Promise.reject(new HarmonyError(HarmonyErrors.HARMONY_OPERATION_FORBIDDEN, 'No streamer provided.'));
            }
            return Promise.resolve(true);
        }, () => {
            // If the playback is not local, the controlled player should manage
            // the restrictions.
            return Promise.resolve(true);
        }, true // Bypass fake state
        );
    }
    /**
     * Activate the current media element.
     */
    _activateElement() {
        // We only need to do this once.
        this._autoActivateElement = false;
        if (this._streamer) {
            this._streamer.activateElement().catch(() => { });
        }
    }
    /**
     * Checks if the command should be executed locally or remotely and
     * call the proper action
     *
     * @param deviceId - The device ID
     * @param localPlaybackAction - A function to run if the target is local.
     * @param remotePlaybackAction - A function to run if the target is remote.
     * @param bypassFakeState - Whether to ignore faked player states.
     * @return A promise boolean.
     */
    _runOnDevice(deviceId = undefined, localPlaybackAction, remotePlaybackAction, bypassFakeState) {
        if (this._hasFakeState && !bypassFakeState) {
            if (this._autoActivateElement) {
                this._activateElement();
            }
            // Claim the state and trigger the remote action instead of local action in
            // This ensures that the state can be negotiated in the backend in case of
            // possible conflicts.
            return this._claimStateAndRun(remotePlaybackAction);
        }
        return this._isTargetIdLocal(deviceId).then((isLocal) => {
            if (isLocal) {
                return this._onStreamerConnect().then(localPlaybackAction);
            }
            return remotePlaybackAction();
        });
    }
    /**
     * Transfers the playback locally and triggers an action.
     *
     * @param action - The action to run.
     * @return A promise that would be fulfilled by the results of the action.
     */
    _claimStateAndRun(action) {
        return this._onStreamerConnect()
            .then(() => {
            this._hasFakeState = false;
            return Promise.all([
                this._controller.transfer(LOCAL_DEVICE_ID, { paused: true }),
                // FIXME: In the future, this should be handled in a better way. Right
                // now we're waiting for the local track to load, but this will trigger
                // if any local track is loaded. The assumption right now is that there
                // would be no state in the streamer at this point, so this kinda works.
                new Promise((resolve, reject) => {
                    if (!this._streamer) {
                        reject(new HarmonyError(HarmonyErrors.HARMONY_OPERATION_FORBIDDEN, 'No streamer provided.'));
                        return;
                    }
                    this._streamer.once("track_loaded" /* STREAMER_TRACK_LOADED */, resolve);
                }),
            ]);
        })
            .then(() => action());
    }
    /**
     * Creates a DeviceInfo object for the local device.
     *
     * @param overrideActive - If set to true, it'll set the device as active.
     * @return The DeviceInfo for the local device.
     */
    _generateLocalDeviceInfo(overrideActive = false) {
        const client = this._client;
        const streamer = this._streamer;
        let currentState = Promise.resolve(false);
        let volume = Promise.resolve(-1);
        if (this._localPlayerEnabled) {
            if (overrideActive) {
                currentState = Promise.resolve(true);
            }
            else if (streamer) {
                currentState = streamer.getCurrentState().then((state) => !!state);
            }
            if (streamer) {
                volume = streamer.getVolume();
            }
        }
        return Promise.all([
            client.getClientDescriptor(),
            client.getSDKId(),
            currentState,
            volume,
        ]).then(([descriptor, version, _currentState, _volume]) => {
            return {
                hidden: this._hidden,
                id: descriptor.id,
                is_active: _currentState,
                is_controllable: this._localPlayerEnabled,
                is_observable: !this._localPlayerEnabled ? false : !this._hidden,
                local: true,
                metadata: descriptor.metadata || {},
                name: descriptor.name,
                type: descriptor.type,
                version: version,
                volume: _volume,
            };
        });
    }
    _movePosition(time, deviceId) {
        return this.getCurrentState().then((state) => {
            if (!state || isNaN(state.position)) {
                return Promise.reject(new HarmonyError(HarmonyErrors.HARMONY_OPERATION_FORBIDDEN, 'Seeking not allowed with invalid track.'));
            }
            const _time = time;
            const newPosition = Math.min(state.duration, Math.max(0, state.position + _time));
            return this.seek(newPosition, deviceId);
        });
    }
    _prepareSkipTrack(track) {
        let result;
        if (typeof track === 'string') {
            // This is assumed to be a UID. A PlayerQueueTrack is assumed to have a
            // URI, but in this case it's not actually required, so we will have to
            // cast this.
            result = queueTrackFromQid(track);
        }
        else {
            result = track;
        }
        return result;
    }
    /**
     * Returns the DeviceDescriptor for the client.
     *
     * @return The DeviceDescriptor of the Harmony instance.
     */
    getClientDescriptor() {
        return this._client.getClientDescriptor();
    }
    /**
     * Returns the ProductState for the current user.
     *
     * The ProductState gives information about the user's product, user flag
     * settings and preferences.
     *
     * @return The ProductState of the current user.
     */
    getProductState() {
        return this._client.getProductState();
    }
    /**
     * Returns a PublicTransport instance.
     *
     * @return A new PublicTransport instance.
     */
    getPublicTransport() {
        return this._client.getPublicTransport();
    }
    /**
     * Returns user information about the currently logged in user.
     *
     * @return The user information for the currently logger in user.
     */
    getUserInfo() {
        return this._client.getUserInfo();
    }
    /**
     * Returns the SDK version.
     *
     * @return The version of the Harmony SDK.
     */
    getVersionDescriptor() {
        return this._client.getVersionDescriptor();
    }
    logAppMetrics(meters, timers) {
        return this._client.logAppMetrics(meters, timers);
    }
    /**
     * Sets the name of the current application or device.
     *
     * This name value will be reflected in the Connect Device Picker of observing
     * Connect devices.
     *
     * @param name - The new device name.
     * @return True if the name was changed successfully, false otherwise.
     */
    setName(name) {
        return this._client.setName(name);
    }
    /**
     * Sets the name of the current application or device according to a template.
     *
     * The following template variables are available (populated from the)
     * browser's User Agent string.
     *
     * - {{name}} - The name of the browser.
     * - {{version}} - The version of the browser.
     * - {{platform}} - The platform where the browser is running.
     *
     * This name value will be reflected in the Connect Device Picker of observing
     * Connect devices.
     *
     * @param name - The name template.
     * @return True if the name was changed successfully, false otherwise.
     */
    setNameTemplate(name) {
        return this._client.setNameTemplate(name);
    }
    /**
     * UNSUPPORTED: DO NOT USE DIRECTLY.
     *
     * @return The ContextPlayerState or null.
     * @deprecated
     */
    getContextPlayerState() {
        return this._controller.getContextPlayerState();
    }
    /**
     * Forces the backend to update the current representation of the context.
     *
     * @param deviceId - The target device id.
     * @param options - Options for the operation.
     * @param options.forceNonUpdateable - Force the operation for non updateable
     *   contexts. Can lead to breaking behaviour.
     * @return True if the context was updated, false otherwise.
     */
    updateCurrentContext(deviceId, options) {
        return this._controller.updateCurrentContext(deviceId, options);
    }
    /**
     * Returns the last known active device, or null if the last known active
     * device has gone offline.
     *
     * @return The last known active device's DeviceInfo or null if the last known
     *   active device has gone offline.
     */
    getLastActiveDevice() {
        if (this._onlyLocalState || this._hasFakeState) {
            return this._generateLocalDeviceInfo(this._hasFakeState);
        }
        return this._controller.getActiveDevice().then((deviceInfo) => {
            if ((deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.local) && this._streamer) {
                deviceInfo.hidden = this._hidden;
                return this._streamer.getVolume().then((volume) => {
                    deviceInfo.volume = volume;
                    return deviceInfo;
                });
            }
            return deviceInfo;
        });
    }
    /**
     * Returns the current PlaybackState, or null if there is nothing playing.
     *
     * @return The current PlaybackState, or null.
     */
    getCurrentState() {
        if (!this._onlyLocalState &&
            (this._preferController || !this._localPlayback)) {
            const noOrphans = !this._localPlayerEnabled || !this._claimInactivePlayerStates;
            return this._controller.getCurrentState(noOrphans).then((state) => {
                // This is a special race where the current device was the last playing
                // device known by the Connect API. Since we do not know whether the
                // last Connect API device is the same local device until we query it,
                // the check for `!this._localPlayback` above will always succeed.
                //
                // This leads to a special race condition where the initial call to
                // `getCurrentState` will contain partial metadata from the connect API
                // but any call immediately afterwards will return null (WSDK-105).
                //
                // To fix this, recheck after we have the data redo the request in case.
                if (!this._preferController && this._localPlayback) {
                    return this.getCurrentState();
                }
                return state;
            });
        }
        return this._onStreamerConnect().then(this._streamer.getCurrentState.bind(this._streamer));
    }
    /**
     * Toggles the playing state of the target device.
     *
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    togglePlay(deviceId) {
        return this._runOnDevice(deviceId, () => this._streamer.togglePlay(), this._controller.togglePlay.bind(this._controller, deviceId));
    }
    /**
     * Pause playback on the target device.
     *
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    pause(deviceId) {
        return this._runOnDevice(deviceId, () => this._streamer.pause(), this._controller.pause.bind(this._controller, deviceId));
    }
    /**
     * Resumes  playback on the target device.
     *
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    resume(deviceId) {
        return this._runOnDevice(deviceId, () => this._streamer.resume(), this._controller.resume.bind(this._controller, deviceId));
    }
    /**
     * Plays the URI on the target device.
     *
     * @param uri - The Spotify URI to play.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @param options - Options for the playback.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    playURI(uri, deviceId, options) {
        return this._canStartNewContext(deviceId).then(() => {
            if (this._autoActivateElement) {
                this._activateElement();
            }
            return this._controller.playURI(uri, deviceId, options);
        });
    }
    /**
     * Plays the tracklist with the uris in the passed array (if the current
     * context can be changed).
     *
     * @param uris - The array contains track URI strings or objects with URIs.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @param options - Options for the playback.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    playTracks(uris, deviceId, options) {
        return this._canStartNewContext(deviceId).then(() => {
            if (this._autoActivateElement) {
                this._activateElement();
            }
            return this._controller.playTracks(uris, deviceId, options);
        });
    }
    /**
     * Skips to the next track in the context.
     *
     * @param deviceId - The target device, or the last active device if not
     * provided or the local device if there is no active device.
     * @param options - The options for the command.
     * @return A promise that will be resolved once the command has been received
     * by the target device.
     */
    nextTrack(deviceId, options) {
        const preparedOptions = {};
        let localCommand = () => this._streamer.nextTrack(Reason.FORWARD_BUTTON);
        const remoteCommand = this._controller.nextTrack.bind(this._controller, deviceId, preparedOptions);
        if (options === null || options === void 0 ? void 0 : options.track) {
            // If there's a provided track, we will need to use a remote command even
            // if the operation is targetting the local device. This is because the
            // track to skip to might not be present in the current state machine
            // window.
            localCommand = remoteCommand;
            preparedOptions.track = this._prepareSkipTrack(options.track);
        }
        return this._runOnDevice(deviceId, localCommand, remoteCommand);
    }
    /**
     * Skips to the previous track if the current track's position is within 3000
     * millisecond, or seeks to the start of the track otherwise.
     *
     * @param deviceId - The target device, or the last active device if not
     * provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     * by the target device.
     */
    smartPreviousTrack(deviceId) {
        const SKIP_PREVIOUS_THRESHOLD = 3000;
        return this.getCurrentState().then((currentState) => {
            if (!currentState) {
                return Promise.reject(new HarmonyError(HarmonyErrors.HARMONY_NO_TRACKS_LOADED, 'No current state.'));
            }
            const disallows = currentState.disallows || {};
            if (disallows.seeking ||
                (currentState.position < SKIP_PREVIOUS_THRESHOLD &&
                    currentState.track_window &&
                    currentState.track_window.previous_tracks &&
                    currentState.track_window.previous_tracks.length > 0)) {
                return this.previousTrack(deviceId);
            }
            return this.seek(0, deviceId);
        });
    }
    /**
     * Skips to the previous track in the context.
     *
     * @param deviceId - The target device, or the last active device if not
     * provided or the local device if there is no active device.
     * @param options - The options for the command
     * @return A promise that will be resolved once the command has been received
     * by the target device.
     */
    previousTrack(deviceId, options) {
        const preparedOptions = {};
        let localCommand = () => this._streamer.previousTrack(Reason.FORWARD_BUTTON);
        const remoteCommand = this._controller.previousTrack.bind(this._controller, deviceId, preparedOptions);
        if (options === null || options === void 0 ? void 0 : options.track) {
            // If there's a provided track, we will need to use a remote command even
            // if the operation is targetting the local device. This is because the
            // track to skip to might not be present in the current state machine
            // window.
            localCommand = remoteCommand;
            preparedOptions.track = this._prepareSkipTrack(options.track);
        }
        return this._runOnDevice(deviceId, localCommand, remoteCommand);
    }
    /**
     * Seeks within the track to the provided position value.
     *
     * @param position - The position in milliseconds within the track to seek.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    seek(position, deviceId) {
        return this._runOnDevice(deviceId, () => this._streamer.seek(position), this._controller.seek.bind(this._controller, position, deviceId));
    }
    /**
     * Adds the provided time to the current track position.
     *
     * @param time - The amount of time, in milliseconds, to add to the  position
     *   of the current track. If not provided, defaults to 15000 millisecond.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    seekForward(time = DEFAULT_SEEK_TIME, deviceId) {
        return this._movePosition(time, deviceId);
    }
    /**
     * Subtracts the provided time to the current track position.
     *
     * @param time - The amount of time, in milliseconds, to subtract from  the
     *   position of the current track. If not provided, defaults to 15000
     *   millisecond.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    seekBackward(time = DEFAULT_SEEK_TIME, deviceId) {
        return this._movePosition(time * -1, deviceId);
    }
    /**
     * Returns the volume of the local device.
     *
     * @return A promise that will be resolved to the volume of the Harmony
     *   instance.
     */
    getVolume() {
        return this._streamer ? this._streamer.getVolume() : Promise.resolve(-1);
    }
    /**
     * Sets the volume of a device.
     *
     * @param volume - The new volume.
     * @param deviceId - The target device, or the last active device if not
     * provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     * by the target device.
     */
    setVolume(volume, deviceId) {
        return this._runOnDevice(deviceId, () => this._streamer.setVolume(volume), this._controller.setVolume.bind(this._controller, volume));
    }
    /**
     * Returns the video profiles for the currently playing video.
     *
     * @return A promise that will be resolved to the video profiles.
     */
    getVideoProfiles() {
        if (!this._streamer) {
            throw new HarmonyError(HarmonyErrors.HARMONY_OPERATION_FORBIDDEN, 'No streamer provided.');
        }
        return this._streamer.getVideoVariants();
    }
    /**
     * Sets the bitrate for the currently playing video.
     *
     * @param value - The bitrate value.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    setPreferredBitrate(value) {
        if (!this._streamer) {
            throw new HarmonyError(HarmonyErrors.HARMONY_OPERATION_FORBIDDEN, 'No streamer provided.');
        }
        return this._streamer.setPreferredBitrate(value);
    }
    /**
     * Returns all of the Connect devices currently registered for the user.
     *
     * Note that this function will guarantee at least one device in the array,
     * which is the local device.
     *
     * @return A promise that will be resolved with an array of DeviceInfo objects.
     */
    getDevices() {
        return this._controller.getDevices().then(this._parseDeviceList.bind(this));
    }
    /**
     * Sets the queue of the currently playing context.
     *
     * @param nextTracks - The next tracks in the list.
     * @param previousTracks - The previous tracks in the list.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     * @deprecated
     */
    setQueue(nextTracks, previousTracks, deviceId) {
        // eslint-disable-next-line no-console
        console.warn('Harmony.setQueue is deprecated; migrate to new PlayerQueue API');
        const operation = this._controller.setQueue.bind(this._controller, nextTracks, previousTracks, deviceId);
        return this._runOnDevice(deviceId, operation, operation);
    }
    /**
     * Adds the provided tracks to the queue.
     *
     * @param nextTracks - The tracks to add to the queue.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     * @deprecated
     */
    addToQueue(nextTracks, deviceId) {
        const tracks = nextTracks.map((track) => typeof track === 'string' ? track : track.uri);
        return this._controller.getQueueManager().addToQueue(tracks, deviceId);
    }
    /**
     * Sets the shuffle mode for the current context.
     *
     * @param shuffle - When set to true, the context will be shuffled.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    setShuffle(shuffle, deviceId) {
        const operation = this._controller.setShuffle.bind(this._controller, shuffle, deviceId);
        return this._runOnDevice(deviceId, operation, operation);
    }
    /**
     * Toggle's the current shuffle mode.
     *
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    toggleShuffle(deviceId) {
        const operation = this._controller.toggleShuffle.bind(this._controller, deviceId);
        return this._runOnDevice(deviceId, operation, operation);
    }
    /**
     * Sets the repeat mode for the current context.
     *
     * @param repeatMode - The repeat mode for the current context.
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    setRepeatMode(repeatMode, deviceId) {
        const operation = this._controller.setRepeatMode.bind(this._controller, repeatMode, deviceId);
        return this._runOnDevice(deviceId, operation, operation);
    }
    /**
     * Cycles through the possible repeat modes for the context.
     *
     * Goes from RepeatMode.NONE to RepeatMode.CONTEXT to RepeatMode.TRACK and
     * back to RepeatMode.NONE.
     *
     * @param deviceId - The target device, or the last active device if not
     *   provided or the local device if there is no active device.
     * @return A promise that will be resolved once the command has been received
     *   by the target device.
     */
    cycleRepeatMode(deviceId) {
        const operation = this._controller.cycleRepeatMode.bind(this._controller, deviceId);
        return this._runOnDevice(deviceId, operation, operation);
    }
    /**
     * Transfers the current context to the target device.
     *
     * @param deviceId - The target device, or the local device if not provided.
     * @param options - The options for the transfer.
     * @param options.paused - If true, the context will be transferred in a
     *   paused state.
     * @return A promise that will be resolved once the command has been received
     * by the target device.
     */
    transfer(deviceId, options) {
        const _deviceId = deviceId || LOCAL_DEVICE_ID;
        const operation = this._controller.transfer.bind(this._controller, _deviceId, options);
        if (this._autoActivateElement) {
            this._activateElement();
        }
        return this._runOnDevice(_deviceId, operation, operation, true);
    }
    /**
     * Sends a logout command to the target device id.
     *
     * @param deviceId - The target device, or the local device if not provided.
     * @return A promise that will be resolved once the command has been received
     * by the target device.
     */
    logout(deviceId) {
        return this._controller.logout(deviceId || LOCAL_DEVICE_ID);
    }
    /**
     * Activates the current media element.
     *
     * Activation is required in order to autoplay on some browsers. Because
     * Harmony playback is asynchronous, the browser interprets playback as a
     * non-user triggered action, causing the playback to pause.
     *
     * Activation primes the media element for the asynchronous playback. This
     * method must be called as part of a synchronous user event such as a click
     * on a button or a list.
     *
     * @return A Promise that will be resolved with a boolean value: `true` if
     *   the element was activated, false otherwise.
     */
    activateElement() {
        if (!this._streamer) {
            return Promise.reject(new HarmonyError(HarmonyErrors.HARMONY_LOCAL_PLAYER_DISABLED, 'Cannot activate element; local player is disabled.'));
        }
        return this._streamer.activateElement();
    }
    /**
     * Returns the Queue Manager for the current Harmony instance, which
     * can be used to get the current state of the queue as well as for operation
     *
     * @return The Player Queue Manager.
     */
    getQueueManager() {
        return this._controller.getQueueManager(
        // This will be the runner for the Play Queue to ensure that the command
        // works with normal harmony operations.
        (command, deviceId) => {
            return this._runOnDevice(deviceId, command, command);
        }, {
            reportInactiveQueues: this._claimInactivePlayerStates,
            onlyLocalQueue: this._onlyLocalState,
            reportInitial: this._hasFakeState || this._localPlayback,
        });
    }
}
//# sourceMappingURL=harmony.js.map